/**
 * The date range on the settings audit trail: days a person picks, instants the API takes.
 *
 * The route filters on instants (`from` inclusive, `to` exclusive), and a publisher picks days.
 * The whole point of this module is that the conversion between the two happens once, here, in the
 * browser's own timezone. Somebody in London asking for "yesterday to today" means their days, and
 * a change made at half past eleven last night is inside that. Building the instants by pasting
 * "T00:00:00Z" onto the chosen dates would quietly drop it, and the same bug at the other end shows
 * a change from the morning before the range starts.
 *
 * So a day becomes an instant through the local Date constructor, which is the only thing that
 * knows what midnight meant on that date, including the nights the clocks change. The end of the
 * range is the start of the day after the last chosen one, because `to` is exclusive.
 *
 * Days are held and shared as `YYYY-MM-DD`, which is what a date input reads and writes and what
 * goes in the address bar. Nothing here formats through Intl: a formatter caches the timezone it
 * was built in, and these labels are built from the numbers a person chose, so they say the day
 * that was chosen whatever else is going on.
 */

/** A range as a publisher chose it: whole local days, both ends inclusive, either end open. */
export interface DayRange {
  readonly from: string | null;
  readonly to: string | null;
}

/** What is sent to the route: instants, `from` inclusive and `to` exclusive, both optional. */
export interface RangeInstants {
  readonly from?: string;
  readonly to?: string;
}

/** No range at all, which is the trail as it has always been shown. */
export const ALL_TIME: DayRange = { from: null, to: null };

/** The ranges worth one click, because they are the questions people actually ask. */
export type RangePreset = 'last_7' | 'last_30' | 'all';

export const RANGE_PRESETS: readonly { preset: RangePreset; label: string }[] = [
  { preset: 'last_7', label: 'Last 7 days' },
  { preset: 'last_30', label: 'Last 30 days' },
  { preset: 'all', label: 'All time' },
];

interface DayParts {
  readonly year: number;
  readonly month: number;
  readonly date: number;
}

/**
 * A `YYYY-MM-DD` day as numbers, or null if it is not one.
 *
 * The round trip through a local Date is the check: it rejects the 31st of a 30 day month and the
 * 29th of February in a year that has no 29th, both of which a hand typed address can carry.
 */
function parts(day: string): DayParts | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const date = Number(match[3]);
  const at = new Date(year, month - 1, date);
  if (at.getFullYear() !== year || at.getMonth() !== month - 1 || at.getDate() !== date) {
    return null;
  }
  return { year, month, date };
}

/** Whether a string is a day this screen can use. */
export function isDay(value: string): boolean {
  return parts(value) !== null;
}

/** A date as the day it falls on where the reader is sitting. */
export function toDay(at: Date): string {
  const year = String(at.getFullYear()).padStart(4, '0');
  const month = String(at.getMonth() + 1).padStart(2, '0');
  const date = String(at.getDate()).padStart(2, '0');
  return `${year}-${month}-${date}`;
}

/** The instant a chosen day starts, locally. */
function startOfDay(day: DayParts): string {
  return new Date(day.year, day.month - 1, day.date).toISOString();
}

/**
 * The instant the day after a chosen day starts, locally.
 *
 * `date + 1` is deliberate: the Date constructor rolls a 31st into the 1st of the next month and a
 * 31st of December into the next year, so there is no month or year arithmetic here to get wrong.
 */
function startOfNextDay(day: DayParts): string {
  return new Date(day.year, day.month - 1, day.date + 1).toISOString();
}

/**
 * What the route is asked for: the start of the first chosen day, and the start of the day after
 * the last one, since `to` is exclusive. An end a person cannot see is the end they meant.
 */
export function rangeInstants(range: DayRange): RangeInstants {
  const from = range.from === null ? null : parts(range.from);
  const to = range.to === null ? null : parts(range.to);
  return {
    ...(from === null ? {} : { from: startOfDay(from) }),
    ...(to === null ? {} : { to: startOfNextDay(to) }),
  };
}

/** Whether anything is being filtered out at all. */
export function isFiltered(range: DayRange): boolean {
  return range.from !== null || range.to !== null;
}

/** The days a preset covers, counted back from the day it is now where the reader is sitting. */
export function presetRange(preset: RangePreset, now: Date = new Date()): DayRange {
  if (preset === 'all') return ALL_TIME;
  const days = preset === 'last_7' ? 7 : 30;
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (days - 1));
  return { from: toDay(start), to: toDay(now) };
}

/**
 * Which preset a range is, if it is one.
 *
 * The address carries the days rather than the name of a preset, so a link sent to a colleague
 * still shows the week it showed when it was sent. This is what puts the button back in its
 * pressed state when that link is opened on the same day.
 */
export function matchingPreset(range: DayRange, now: Date = new Date()): RangePreset | null {
  for (const { preset } of RANGE_PRESETS) {
    const candidate = presetRange(preset, now);
    if (candidate.from === range.from && candidate.to === range.to) return preset;
  }
  return null;
}

/** A range out of the address, ignoring anything that is not a day rather than failing on it. */
export function rangeFromParams(params: URLSearchParams): DayRange {
  const from = params.get('from');
  const to = params.get('to');
  return {
    from: from !== null && isDay(from) ? from : null,
    to: to !== null && isDay(to) ? to : null,
  };
}

/** The address for a range: the days themselves, and nothing when there is no range. */
export function paramsForRange(range: DayRange, existing?: URLSearchParams): URLSearchParams {
  const params = new URLSearchParams(existing);
  if (range.from === null) params.delete('from');
  else params.set('from', range.from);
  if (range.to === null) params.delete('to');
  else params.set('to', range.to);
  return params;
}

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function dayLabel(day: DayParts, withYear = true): string {
  const month = MONTHS[day.month - 1] ?? String(day.month);
  return withYear ? `${day.date} ${month} ${day.year}` : `${day.date} ${month}`;
}

/**
 * The range in words, as the tail of a sentence: "between 1 and 8 September 2026".
 *
 * Null means no range, which is a different sentence rather than a shorter one, so the screen
 * writes that itself. This exists so "what is on screen" and "nothing was found" are said in the
 * same words, because the second one is where somebody could mistake a filtered view for the whole
 * trail.
 */
export function rangeWords(range: DayRange): string | null {
  const from = range.from === null ? null : parts(range.from);
  const to = range.to === null ? null : parts(range.to);

  if (from === null) {
    if (to === null) return null;
    return `up to and including ${dayLabel(to)}`;
  }
  if (to === null) return `from ${dayLabel(from)} onwards`;

  if (from.year === to.year && from.month === to.month && from.date === to.date) {
    return `on ${dayLabel(from)}`;
  }
  const sameMonth = from.year === to.year && from.month === to.month;
  const sameYear = from.year === to.year;
  const start = sameMonth ? String(from.date) : dayLabel(from, !sameYear);
  return `between ${start} and ${dayLabel(to)}`;
}
